import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../lib/constants';
import api from '../utils/api';
import {
  LayoutDashboard,
  Ticket,
  Package,
  ShoppingCart,
  Receipt,
  Users,
  Building2,
  Box,
  CreditCard,
  LogOut,
  Hotel,
  ArrowRightLeft,
  CalendarClock,
  Activity,
  HeartPulse,
  Timer,
  Archive as ArchiveIcon,
  Menu,
  X,
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';

const Sidebar = ({ mobileOpen, setMobileOpen }) => {
  const { user, logout, hasPermission, hasAnyPermission } = useAuth();
  const navigate = useNavigate();
  const [pendingCounts, setPendingCounts] = useState({ tickets: 0, purchase_requests: 0 });
  const [mentionSummary, setMentionSummary] = useState({ unread_total: 0, by_ticket: {} });

  const canApproveTicket = hasPermission('approve_ticket');
  const canApprovePR = hasPermission('approve_purchase_request');
  const shouldPoll = canApproveTicket || canApprovePR;

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      try {
        if (shouldPoll) {
          const res = await api.get('/dashboard/pending-approvals');
          if (!cancelled) setPendingCounts(res.data || { tickets: 0, purchase_requests: 0 });
        }
        const mres = await api.get('/notifications/summary');
        if (!cancelled) setMentionSummary(mres.data || { unread_total: 0, by_ticket: {} });
      } catch (_) { /* silent */ }
    };
    fetchAll();
    const id = setInterval(fetchAll, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [shouldPoll]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getMenuItems = () => {
    const items = [
      { path: '/', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/tickets', label: 'Tiket', icon: Ticket,
        badge: (canApproveTicket ? pendingCounts.tickets : 0) + (mentionSummary.unread_total || 0) },
      { path: '/schedule', label: 'Jadwal', icon: CalendarClock },
    ];

    if (hasPermission('view_warehouse')) {
      items.push({ path: '/warehouse', label: 'Gudang & Inventory', icon: Package });
      items.push({ path: '/stock-flow', label: 'Arus Stok', icon: Activity });
    }
    if (hasPermission('transfer_stock')) {
      items.push({ path: '/transfer', label: 'Transfer Stok', icon: ArrowRightLeft });
    }
    if (hasPermission('manage_items')) {
      items.push({ path: '/items', label: 'Master Barang', icon: Box });
    }

    if (hasAnyPermission(['submit_purchase_request', 'manage_purchasing', 'approve_purchase_request', 'order_purchase_request', 'receive_purchase_request'])) {
      items.push({ path: '/purchasing', label: 'Purchasing', icon: ShoppingCart,
        badge: canApprovePR ? pendingCounts.purchase_requests : 0 });
    }

    if (hasAnyPermission(['view_invoices', 'generate_invoice', 'mark_paid_invoice'])) {
      items.push({ path: '/invoices', label: 'Invoice', icon: Receipt });
    }

    if (hasPermission('manage_users')) {
      items.push({ path: '/users', label: 'Manajemen User', icon: Users });
    }
    if (hasPermission('manage_locations')) {
      items.push({ path: '/locations', label: 'Lokasi Cabang', icon: Building2 });
    }
    if (hasAnyPermission(['manage_bank_accounts','view_invoices','generate_invoice'])) {
      items.push({ path: '/bank-accounts', label: 'Daftar Rekening', icon: CreditCard });
    }
    if (hasPermission('view_dashboard')) {
      items.push({ path: '/sla-dashboard', label: 'SLA Monitoring', icon: Timer });
    }
    if (hasPermission('view_receipt_archive')) {
      items.push({ path: '/archive', label: 'Arsip Bukti', icon: ArchiveIcon });
    }
    if (user?.role === 'super_admin') {
      items.push({ path: '/system-health', label: 'System Health', icon: HeartPulse });
    }

    return items;
  };

  const menuItems = getMenuItems();

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();
  };

  const handleNavClick = () => {
    if (setMobileOpen) setMobileOpen(false);
  };

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
      
      <aside
        className={`
          w-64 bg-card border-r border-border h-screen flex flex-col z-50
          fixed lg:sticky top-0 left-0
          transform transition-transform duration-200 ease-in-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="main-sidebar"
      >
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <Hotel className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="font-semibold text-lg tracking-tight text-foreground">HotelDesk</h1>
              <p className="text-xs text-muted-foreground">Sistem Ticketing</p>
            </div>
          </div>
          <button
            className="lg:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="sidebar-close-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-secondary'
                }`
              }
              data-testid={`sidebar-link-${item.path.replace('/', '') || 'dashboard'}`}
            >
              <item.icon className="w-4 h-4" />
              <span className="flex-1">{item.label}</span>
              {item.badge > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
                  data-testid={`sidebar-badge-${item.path.replace('/', '') || 'dashboard'}`}
                >
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 mb-3">
            <Avatar className="w-9 h-9">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate" data-testid="user-role">
                {ROLES[user?.role] || user?.role}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Keluar
          </Button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
